/**
 * AAMI ST108 Compliance Resource Page — B2B SEO flagship.
 *
 * This page targets high-intent facility-manager / biomed queries like:
 *   • "AAMI ST108 water purification systems"
 *   • "AAMI ST108 category 1 water"
 *   • "AAMI ST108 vs TIR34"
 *   • "Joint Commission water compliance"
 *
 * Structured as a TechnicalArticle so Google indexes it as authoritative
 * technical content (biomedical engineers, SPD managers).
 */
import SEO, { technicalArticleSchema } from "@/components/SEO";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { CheckCircle2, Droplets, FileText, ShieldCheck, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

const WATER_CATEGORIES = [
  {
    cat: "Category 1",
    name: "Utility Water",
    use: "Pre-cleaning, decontamination, first stages of instrument cleaning",
    max_hardness: "≤ 150 mg/L (CaCO₃)",
    max_conductivity: "≤ 500 µS/cm",
    endotoxin: "Not specified",
  },
  {
    cat: "Category 2",
    name: "Critical Water",
    use: "Final rinse for reusable medical devices, terminal rinse for endoscope reprocessing",
    max_hardness: "≤ 1 mg/L",
    max_conductivity: "≤ 10 µS/cm",
    endotoxin: "≤ 10 EU/mL",
  },
  {
    cat: "Category 3",
    name: "Sterile Water for Injection",
    use: "Critical rinse where residual water can enter a sterile compartment",
    max_hardness: "Trace",
    max_conductivity: "≤ 1.3 µS/cm",
    endotoxin: "≤ 0.25 EU/mL",
  },
];

const FAQS = [
  {
    q: "What is AAMI ST108?",
    a: "AAMI ST108:2023 is the current U.S. consensus standard for water quality used in medical-device processing. It replaced the water sections of AAMI TIR34 and defines three categories of processing water (utility, critical, and sterile-for-injection) plus their required physical, chemical, and microbial limits. Compliance is expected by Joint Commission surveyors, DNV, and CMS.",
  },
  {
    q: "How is AAMI ST108 different from TIR34?",
    a: "AAMI TIR34 was a Technical Information Report (guidance). AAMI ST108 is a formal standard (mandatory language). ST108 replaces TIR34's water-quality content and adds explicit numeric limits, testing frequency, documentation, and staff-competency requirements. Facilities using TIR34 as their reference document should transition to ST108.",
  },
  {
    q: "Do all hospitals have to comply with ST108?",
    a: "Any facility that reprocesses reusable medical devices — hospitals, ambulatory surgery centers, outpatient clinics, dental practices with sterile-processing — is expected to meet ST108. Joint Commission, DNV, and CMS surveyors are actively citing ST108 non-conformance under EC.02.05.01 (utility systems) and IC.02.02.01 (medical equipment reprocessing).",
  },
  {
    q: "What water categories does ST108 define?",
    a: "Three categories: (1) Utility Water for pre-cleaning; (2) Critical Water for terminal rinse of reusable devices and endoscope reprocessing; (3) Sterile Water for Injection where residual water could contact a sterile compartment. Each has explicit hardness, conductivity, TOC, endotoxin, and bacterial limits.",
  },
  {
    q: "How does Sterile Mate help hospitals meet ST108?",
    a: "Sterile Mate provides ST108-compliant central RO systems, DI polishing tanks, DI tank exchange service, endotoxin/TOC/conductivity test kits, water-system preventative maintenance, and full compliance-audit documentation. We serve hospitals across Texas, Louisiana, Alabama, and Mississippi with same-day dispatch from Houston.",
  },
];

export default function AamiSt108Page() {
  const canonical = "/resources/aami-st108";
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      technicalArticleSchema({
        headline: "AAMI ST108 Water Purification for Sterile Processing — 2024 Compliance Guide",
        description:
          "Complete AAMI ST108:2023 compliance guide for hospital sterile-processing water systems. Water categories, testing limits, common survey findings, and how to source ST108-compliant central RO, DI tank exchange, and preventative-maintenance services.",
        url: canonical,
        datePublished: "2026-01-15",
        dateModified: "2026-02-23",
        keywords: [
          "AAMI ST108",
          "AAMI ST108 water purification",
          "AAMI ST108 category 1 water",
          "AAMI ST108 category 2 water",
          "AAMI ST108 vs TIR34",
          "Joint Commission water compliance",
          "sterile processing water",
          "central RO system SPD",
        ],
      }),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="aami-st108-page">
      <SEO
        canonical={canonical}
        title="AAMI ST108 Water Purification for Sterile Processing — Compliance Guide"
        description="Complete AAMI ST108:2023 water compliance guide for hospital sterile processing. Water categories, testing limits, Joint Commission survey findings, and Gulf-region service coverage."
        keywords={[
          "AAMI ST108 water purification systems",
          "AAMI ST108 category 1 water",
          "AAMI ST108 vs TIR34",
          "Joint Commission water compliance",
          "central reverse osmosis SPD",
          "hemodialysis water treatment",
          "medical DI tank exchange",
        ]}
        schema={schema}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#0a1a35] via-[#0b2447] to-[#0056A8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.18),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-cyan-300 mb-4">
            Compliance Resource · 2024 Update
          </p>
          <h1 className="text-4xl md:text-6xl font-black leading-[1.05] max-w-4xl">
            AAMI ST108 Water Purification for Sterile Processing.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">
            The complete 2024 compliance guide for hospital sterile-processing water systems.
            Category 1, 2, and 3 water limits, Joint Commission survey findings, and how to source
            ST108-compliant central RO, DI tank exchange, and preventative maintenance across{" "}
            <strong>Texas, Louisiana, Alabama, and Mississippi.</strong>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
              <Link to="/marketplace/di-tank-exchange">
                <Droplets className="w-5 h-5 mr-2" />
                DI Tank Exchange Service
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              <a href="mailto:info@sterilemate.com?subject=AAMI ST108 compliance audit">
                <FileText className="w-5 h-5 mr-2" />
                Request Compliance Audit
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* WATER CATEGORIES TABLE */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-2">Water Categories Defined by AAMI ST108:2023</h2>
        <p className="text-slate-600 mb-8">
          ST108 defines three categories of processing water. Each has explicit hardness, conductivity, TOC, and endotoxin limits.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-800">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Category</th>
                <th className="text-left px-4 py-3 font-bold">Typical Use</th>
                <th className="text-left px-4 py-3 font-bold">Max Hardness</th>
                <th className="text-left px-4 py-3 font-bold">Max Conductivity</th>
                <th className="text-left px-4 py-3 font-bold">Max Endotoxin</th>
              </tr>
            </thead>
            <tbody>
              {WATER_CATEGORIES.map((r) => (
                <tr key={r.cat} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    <span className="block">{r.cat}</span>
                    <span className="text-xs text-blue-700">{r.name}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.use}</td>
                  <td className="px-4 py-3 font-mono text-slate-800">{r.max_hardness}</td>
                  <td className="px-4 py-3 font-mono text-slate-800">{r.max_conductivity}</td>
                  <td className="px-4 py-3 font-mono text-slate-800">{r.endotoxin}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Source: ANSI/AAMI ST108:2023, Water for the Processing of Medical Devices. Consult the full standard for complete specifications.
        </p>
      </section>

      {/* HOW WE HELP */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-black text-slate-900 mb-2">How Sterile Mate Helps You Comply</h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            Six years serving hospitals across the Gulf region — Texas Medical Center to critical-access
            facilities in rural Mississippi.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                icon: Droplets,
                title: "Central RO & DI Systems",
                text: "AAMI ST108–compliant central reverse-osmosis plus DI polishing loops for SPD, endoscopy, and pharmacy compounding.",
              },
              {
                icon: ShieldCheck,
                title: "DI Tank Exchange",
                text: "Next-day medical-grade DI tank swaps — no contracts, cGMP + ISO 9001 regen facility.",
              },
              {
                icon: CheckCircle2,
                title: "Preventative Maintenance",
                text: "Scheduled RO membrane cleaning, quarterly TOC/conductivity/endotoxin testing, and full survey-ready documentation.",
              },
            ].map((f) => (
              <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <f.icon className="w-6 h-6 text-blue-700 mb-3" />
                <p className="font-bold text-slate-900 mb-1">{f.title}</p>
                <p className="text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-slate-200 bg-white p-5 open:shadow-md transition-shadow"
            >
              <summary className="cursor-pointer flex items-center justify-between font-bold text-slate-900">
                {f.q}
                <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-slate-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Survey warning banner */}
      <section className="bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-6 py-8 flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-900 mb-1">
              Joint Commission is actively citing ST108 non-conformance.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              Since 2024, surveyors are writing findings under EC.02.05.01 and IC.02.02.01 for
              facilities that cannot demonstrate ST108-compliant water quality, testing
              frequency, or documentation. If you are unsure whether your facility meets ST108,{" "}
              <a href="mailto:info@sterilemate.com?subject=AAMI ST108 compliance audit" className="font-semibold text-amber-800 underline">
                request a compliance audit
              </a>{" "}
              — we typically respond within one business day.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
